/**
 * Feature requests as the runtime screens read them.
 *
 * Feature requests live in the control plane. The runtime's
 * `/api/feature-requests` routes pass its rows through unchanged, in camelCase
 * (`id`, `ticketNumber`, `voteCount`, `userVoted`, `authorName`, `createdAt`,
 * and comments with `content`, `isAdmin`; docs/api/runtime/openapi.md
 * § Feature requests). PATs read that shape too, so the API keeps it. The
 * Feature Requests page and Prompt Forge were written against the runtime's
 * old snake_case documents (`request_id`, `ticket_number`, `vote_count`, …),
 * so against the control plane they showed blank ticket numbers, opened
 * `/feature-requests/undefined`, and the page's search threw.
 *
 * This module is the one place that maps either spelling to the shape those
 * screens use. Keys already in the snake_case shape win; nothing is removed.
 */

/** The first of `keys` that `obj` has a value for (null and undefined skipped). */
function first(obj, ...keys) {
    for (const key of keys) {
        const value = obj?.[key];
        if (value !== undefined && value !== null) return value;
    }
    return undefined;
}

const count = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
};

export function normalizeComment(raw) {
    if (!raw || typeof raw !== 'object') return raw;
    return {
        ...raw,
        comment_id: first(raw, 'comment_id', 'id'),
        user_name: first(raw, 'user_name', 'author_name', 'authorName', 'author_email', 'authorEmail') ?? 'Unknown',
        text: first(raw, 'text', 'content') ?? '',
        is_admin: Boolean(first(raw, 'is_admin', 'isAdmin')),
        is_system: Boolean(first(raw, 'is_system', 'isSystem')),
        created_at: first(raw, 'created_at', 'createdAt'),
    };
}

/**
 * One request in the page's shape. `user_voted` stays undefined when the row
 * doesn't say (the control plane's single-request read carries no vote state),
 * so a caller can fall back to what the list knows.
 */
export function normalizeRequest(raw) {
    if (!raw || typeof raw !== 'object') return raw;
    const voted = first(raw, 'user_voted', 'userVoted');
    return {
        ...raw,
        request_id: first(raw, 'request_id', 'id'),
        ticket_number: first(raw, 'ticket_number', 'ticketNumber') ?? '',
        title: raw.title ?? '',
        description: raw.description ?? '',
        vote_count: count(first(raw, 'vote_count', 'voteCount') ?? raw._count?.votes),
        comment_count: count(first(raw, 'comment_count', 'commentCount') ?? raw._count?.comments),
        attachment_count: count(first(raw, 'attachment_count', 'attachmentCount')),
        user_voted: voted === undefined ? undefined : Boolean(voted),
        author_name: first(raw, 'author_name', 'authorName', 'author_email', 'authorEmail') ?? '',
        created_at: first(raw, 'created_at', 'createdAt'),
        comments: Array.isArray(raw.comments) ? raw.comments.map(normalizeComment) : raw.comments,
    };
}

/** A list answer (an array, or `{data: [...]}`) as an array in the page's shape. */
export function normalizeRequestList(data) {
    const rows = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
    return rows.map(normalizeRequest);
}

/**
 * What Prompt Forge shows once a ticket is filed: its number and a link to the
 * Feature Requests page. Read through the page's shape, so the control plane's
 * `ticketNumber` / `id` fill it.
 */
export function promptForgeTicket(raw, target, origin = '') {
    const doc = normalizeRequest(raw) || {};
    const ref = doc.ticket_number || doc.request_id || '';
    return {
        kind: target === 'bug' ? 'BUG' : 'FR',
        id: ref,
        label: doc.ticket_number || 'Ticket',
        url: `${origin}/feature-requests?ticket=${encodeURIComponent(ref)}`,
    };
}

/**
 * The request after a vote toggle. The control plane answers `{voted}` only,
 * so the count moves by one unless the answer carries a count of its own.
 */
export function applyVote(request, answer) {
    const voted = Boolean(answer?.voted);
    const answered = first(answer, 'vote_count', 'voteCount');
    // A toggle that answers voted=true added a vote; voted=false took one away.
    const was = request.user_voted;
    const delta = was === undefined || Boolean(was) !== voted ? (voted ? 1 : -1) : 0;
    const vote_count = answered !== undefined ? count(answered) : Math.max(0, count(request.vote_count) + delta);
    return { ...request, user_voted: voted, vote_count };
}
